import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    pdf,
} from '@react-pdf/renderer';
import PDFHeader from './PDFHeader';

// Enhanced Styles
const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        padding: 20,
        fontSize: 10,
        backgroundColor: '#f9f9f9',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: 5,
        padding: 15,
        marginBottom: 15,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    header: {
        textAlign: 'center',
        marginBottom: 15,
    },
    hotelName: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 5,
        color: '#2c3e50',
    },
    hotelAddress: {
        fontSize: 10,
        marginBottom: 3,
        color: '#7f8c8d',
    },
    hotelContact: {
        fontSize: 10,
        marginBottom: 5,
        color: '#7f8c8d',
    },
    gst: {
        fontSize: 10,
        marginBottom: 15,
        color: '#7f8c8d',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 10,
        color: '#2c3e50',
        borderBottom: '1 solid #eaeaea',
        paddingBottom: 5,
        marginTop: 10,
    },
    mainBorder: {
        border: '1 solid #e0e0e0',
        borderRadius: 4,
        padding: 15,
        marginTop: 10,
        backgroundColor: '#fff',
    },
    headerRow: {
        flexDirection: 'row',
        borderBottom: '1 solid #e0e0e0',
        paddingBottom: 8,
        marginBottom: 8,
    },
    headerCell: {
        flex: 1,
        fontWeight: 'bold',
        fontSize: 11,
        textAlign: 'center',
        color: '#2c3e50',
    },
    guestNameRow: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        marginBottom: 8,
        paddingHorizontal: 5,
    },
    guestNameText: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#34495e',
    },
    checkinDetails: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
        paddingHorizontal: 5,
    },
    label: {
        fontWeight: 'bold',
        color: '#34495e',
    },
    table: {
        border: '1 solid #e0e0e0',
        borderRadius: 3,
        marginTop: 10,
        marginBottom: 15,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#3498db',
        paddingVertical: 6,
    },
    tableHeaderCell: {
        paddingHorizontal: 6,
        textAlign: 'center',
        flex: 1,
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 10,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottom: '1 solid #e0e0e0',
        paddingVertical: 6,
        backgroundColor: '#fff',
    },
    tableRowAlternate: {
        backgroundColor: '#f8f9fa',
    },
    cell: {
        paddingHorizontal: 6,
        textAlign: 'center',
        flex: 1,
        fontSize: 9,
        color: '#2c3e50',
    },
    cellLeft: {
        textAlign: 'left',
        flex: 2,
    },
    cellHighlight: {
        fontWeight: 'bold',
        color: '#e74c3c',
    },
    totalsContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingTop: 10,
    },
    totalsBox: {
        width: '50%',
        border: '1 solid #e0e0e0',
        borderRadius: 3,
        padding: 10,
        backgroundColor: '#f8f9fa',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
    },
    totalLabel: {
        fontWeight: '600',
        color: '#7f8c8d',
    },
    totalValue: {
        fontWeight: 'bold',
        color: '#2c3e50',
    },
    grandTotal: {
        borderTop: '1 solid #e0e0e0',
        marginTop: 5,
        paddingTop: 5,
        fontWeight: 'bold',
        color: '#e74c3c',
    },
    amountInWords: {
        marginTop: 10,
        fontStyle: 'italic',
        fontSize: 9,
        color: '#7f8c8d',
        padding: 8,
        backgroundColor: '#f8f9fa',
        borderRadius: 3,
    },
    signatureArea: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 30,
        paddingTop: 15,
        borderTop: '1 dashed #e0e0e0',
    },
    signatureBox: {
        width: '40%',
    },
    signatureLabel: {
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#7f8c8d',
    },
});

// Enhanced Number to Words Function
const numberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num === 0) return 'Zero';
    if (num < 10) return units[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + units[num % 10] : '');

    if (num < 1000) {
        return units[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
    }

    if (num < 100000) {
        return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
    }

    if (num < 10000000) {
        return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + numberToWords(num % 100000) : '');
    }

    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + numberToWords(num % 10000000) : '');
};

const ReservationBillDocument = ({ billData, reservation, hotel }) => {
    const room = reservation?.room;
    const guest = reservation?.guest;
    const checkIn = new Date(reservation?.check_in);
    const checkOut = new Date(reservation?.check_out);
    const duration = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    const perDayRate = (billData?.total_amount || room?.price || 1500);
    const totalAmount = perDayRate * duration;

    // Updated GST calculation based on room rate
    const gstRate = perDayRate <= 7500 ? 0.12 : 0.18;
    const cgst = Math.round(totalAmount * gstRate / 2);
    const sgst = Math.round(totalAmount * gstRate / 2);
    const grandTotal = totalAmount + cgst + sgst;

    // Get the GST rate percentage for display
    const gstPercentage = gstRate * 100;
    const cgstPercentage = gstPercentage / 2;
    const sgstPercentage = gstPercentage / 2;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles?.card}>
                    {/* Header with hotel info */}
                    <View >
                        <PDFHeader data={hotel} />
                    </View>

                    <View >
                        <Text style={styles.sectionTitle}>Reservation Details</Text>
                        <View style={styles.headerRow}>
                            <Text style={styles.headerCell}>Guest Details</Text>
                            <Text style={styles.headerCell}>Room No: {room?.name || '-'}</Text>
                            <Text style={styles.headerCell}>Bill No: NRG001</Text>
                        </View>

                        <View style={styles.guestNameRow}>
                            <Text style={styles.guestNameText}>{guest?.name || 'Guest Name'}</Text>
                        </View>

                        <View style={styles.checkinDetails}>
                            <View>
                                <Text style={styles.label}>Check-In</Text>
                                <Text>Date: {checkIn.toLocaleDateString()}</Text>
                                <Text>Time: {checkIn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                            </View>
                            <View>
                                <Text style={styles.label}>Check-Out</Text>
                                <Text>Date: {checkOut.toLocaleDateString()}</Text>
                                <Text>Time: {checkOut.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                            </View>
                        </View>

                        {/* Enhanced Table */}
                        <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>S.No</Text>
                                <Text style={[styles.tableHeaderCell, styles.cellLeft]}>Description</Text>
                                <Text style={styles.tableHeaderCell}>Capacity</Text>
                                <Text style={styles.tableHeaderCell}>Rate (Rs.)</Text>
                                <Text style={styles.tableHeaderCell}>Days</Text>
                                <Text style={styles.tableHeaderCell}>Total (Rs.)</Text>
                            </View>

                            <View style={styles.tableRow}>
                                <Text style={styles.cell}>1</Text>
                                <Text style={[styles.cell, styles.cellLeft]}>Room Charges</Text>
                                <Text style={styles.cell}>{room?.capacity || 2}</Text>
                                <Text style={styles.cell}>{perDayRate.toLocaleString('en-IN')}.00</Text>
                                <Text style={styles.cell}>{duration}</Text>
                                <Text style={[styles.cell, styles.cellHighlight]}>{totalAmount.toLocaleString('en-IN')}.00</Text>
                            </View>
                        </View>

                        {/* Totals section */}
                        <View style={styles.totalsContainer}>
                            <View style={styles.totalsBox}>
                                <View style={styles.totalRow}>
                                    <Text style={styles.totalLabel}>Room Charges:</Text>
                                    <Text style={styles.totalValue}>Rs. {totalAmount.toLocaleString('en-IN')}.00</Text>
                                </View>
                                <View style={styles.totalRow}>
                                    <Text style={styles.totalLabel}>CGST {cgstPercentage}%:</Text>
                                    <Text style={styles.totalValue}>Rs. {cgst}.00</Text>
                                </View>
                                <View style={styles.totalRow}>
                                    <Text style={styles.totalLabel}>SGST {sgstPercentage}%:</Text>
                                    <Text style={styles.totalValue}>Rs. {sgst}.00</Text>
                                </View>
                                <View style={[styles.totalRow, styles.grandTotal]}>
                                    <Text style={styles.totalLabel}>GRAND TOTAL:</Text>
                                    <Text style={styles.totalValue}>Rs. {grandTotal.toLocaleString('en-IN')}.00</Text>
                                </View>
                            </View>
                        </View>

                        {/* Amount in words */}
                        <Text style={styles.amountInWords}>
                            Amount in Words: {numberToWords(grandTotal)} Rupees Only
                        </Text>
                    </View>

                    {/* Footer with terms and signature */}
                    <View >
                        <Text style={styles.sectionTitle}>Terms & Conditions</Text>
                        <Text>1. Check-in/Check-out time is 12:00 PM. Early check-in/late check-out subject to availability.</Text>
                        <Text>2. Government approved photo ID proof is mandatory for all guests.</Text>
                        <Text>3. Visitors are not permitted in guest rooms after 11:00 PM.</Text>
                        <Text>4. Room service available from 7:00 AM to 10:00 PM.</Text>

                        <View style={styles.signatureArea}>
                            <View style={styles.signatureBox}>
                                <Text style={styles.signatureLabel}>Guest Signature</Text>
                                <Text>_________________________</Text>
                            </View>
                            <View style={styles.signatureBox}>
                                <Text style={styles.signatureLabel}>Authorized Signature</Text>
                                <Text>_________________________</Text>
                                <Text>Manager</Text>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default ReservationBillDocument;

export const downloadPDF = async (billData, reservation, hotel) => {
    const doc = <ReservationBillDocument billData={billData} reservation={reservation} hotel={hotel} />;
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Reservation_${reservation.id}_Receipt.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};